import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AnyAction} from 'redux';
import {useSaveRunfile} from '../../../../../hooks/useSaveRunfile';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import {useImageMarkupContext} from './ImageMarkupContext';
import {IElementImageMarkup, IShape} from '../../../../../incase-domain/elements/question/imageMarkup';
import {MarkupImageElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {IGameConfig} from '../../../../../incase-domain/proptypes/gameConfig';
import {getNewPoints} from '../../../../../service/gamification.service';
import {handleElementsClickedAction} from '../../../../../store/actions/chalkBoardElements.actions';
import {
    getPixelData,
    getRasterizedShapesIntersection,
    isPointInsideArea,
    getPolygonIntersectionPercentage,
    isPointInPolygon,
} from '../../../../../util/geometry';
import {isQuestionRetriable, isQuestionRetryLimitReached} from '../../../../../util/util';
import {IPresentation} from '../../../../../incase-domain/presentation';

const MIN_POLYGON_VERTICES = 3;
const DEFAULT_IMAGE_DIMENSION = 0;
const DEFAULT_POINTS = 0;

interface MarkupRefs {
    imageRef: React.RefObject<HTMLImageElement>;
    canvasesRef: React.MutableRefObject<Map<number, HTMLCanvasElement> | null>;
}

interface ValidationContext {
    element: IElementImageMarkup;
    canvas: HTMLCanvasElement | undefined;
    markedArea: IShape;
    imageRef: React.RefObject<HTMLImageElement>;
}

const getCanvasesMap = (canvasesRef: React.MutableRefObject<Map<number, HTMLCanvasElement> | null>): Map<number, HTMLCanvasElement> => {
    return canvasesRef.current ?? new Map();
};

const isValidPolygon = (points: Array<{x: number; y: number}>): boolean => {
    return points && points.length >= MIN_POLYGON_VERTICES;
};

const getCanvasForMarkedArea = (
    canvasesRef: React.MutableRefObject<Map<number, HTMLCanvasElement> | null>,
    index: number
): HTMLCanvasElement | undefined => {
    const canvases = getCanvasesMap(canvasesRef);
    return Array.from(canvases.values())[index];
};

const scalePointToImageCoordinates = (
    point: {x: number; y: number},
    imageRef: React.RefObject<HTMLImageElement>
): {x: number; y: number} => {
    return {
        x: Math.round(point.x * (imageRef.current?.width || DEFAULT_IMAGE_DIMENSION)),
        y: Math.round(point.y * (imageRef.current?.height || DEFAULT_IMAGE_DIMENSION)),
    };
};

const checkIntersectionWithRaster = (shape: IShape, canvas: HTMLCanvasElement | undefined, minIntersection: number): boolean => {
    if (!canvas) return false;

    const correctRaster = getPixelData(canvas);
    const intersectionPercentage = getRasterizedShapesIntersection(shape.raster, correctRaster);
    return intersectionPercentage > minIntersection;
};

const checkIntersectionWithPolygon = (
    shapePoints: Array<{x: number; y: number}>,
    markedAreaPoints: Array<{x: number; y: number}>,
    minIntersection: number
): boolean => {
    const intersectionPercentage = getPolygonIntersectionPercentage(shapePoints, markedAreaPoints);
    return intersectionPercentage > minIntersection;
};

const checkPointInRaster = (
    point: {x: number; y: number},
    canvas: HTMLCanvasElement | undefined,
    imageRef: React.RefObject<HTMLImageElement>
): boolean => {
    if (!canvas) return false;

    const correctRaster = getPixelData(canvas);
    const scaledPoint = scalePointToImageCoordinates(point, imageRef);
    return isPointInsideArea(scaledPoint, correctRaster);
};

const isShapeCorrectByIntersection = (shape: IShape, context: ValidationContext): boolean => {
    const {element, canvas, markedArea} = context;

    // Backwards compatibility: fallback to raster if shape has it (old data)
    if (shape.raster && shape.raster.length > 0) {
        return checkIntersectionWithRaster(shape, canvas, element.correctAnswerMinIntersection);
    }
    if (isValidPolygon(shape.points) && isValidPolygon(markedArea.points)) {
        return checkIntersectionWithPolygon(shape.points, markedArea.points, element.correctAnswerMinIntersection);
    }

    return false;
};

const isShapeCorrectByPointInPolygon = (shape: IShape, context: ValidationContext): boolean => {
    const {canvas, markedArea, imageRef} = context;

    // New approach: use polygon geometry if available
    if (isValidPolygon(markedArea.points)) {
        const firstPoint = shape.points[0];
        return isPointInPolygon(firstPoint, markedArea.points);
    }
    if (canvas) {
        // Legacy: use raster for old data
        return checkPointInRaster(shape.points[0], canvas, imageRef);
    }

    return false;
};

const validateShape = (shape: IShape, context: ValidationContext): boolean => {
    const {element} = context;

    return element.intersectionAnswerType ? isShapeCorrectByIntersection(shape, context) : isShapeCorrectByPointInPolygon(shape, context);
};

const validateUserShapes = (
    userMarkedShapes: IShape[],
    element: IElementImageMarkup,
    markupRefs: MarkupRefs
): {answers: IShape[]; correctAnswers: number[]} => {
    const {imageRef, canvasesRef} = markupRefs;
    const {markedAreas} = element;

    const answers: IShape[] = userMarkedShapes.map((shape) => ({
        points: shape.points,
        isCorrect: false,
    }));

    if (element.isDebatable) {
        return {answers, correctAnswers: []};
    }

    const coveredAreas = new Set<number>();

    userMarkedShapes.forEach((shape, shapeIndex) => {
        for (let markedAreaIndex = 0; markedAreaIndex < markedAreas.length; markedAreaIndex++) {
            const canvas = getCanvasForMarkedArea(canvasesRef, markedAreaIndex);
            const markedArea = markedAreas[markedAreaIndex];

            const context: ValidationContext = {
                element,
                canvas,
                markedArea,
                imageRef,
            };

            const isCorrect = validateShape(shape, context);

            if (isCorrect) {
                coveredAreas.add(markedAreaIndex);
                answers[shapeIndex] = {
                    ...answers[shapeIndex],
                    isCorrect: true,
                    markedAreaIndex,
                };
                break;
            }
        }
    });

    return {answers, correctAnswers: Array.from(coveredAreas)};
};

const shouldFinalizeAnswer = (
    element: IElementImageMarkup,
    elementInteractions: MarkupImageElementInteractions,
    presentation: IPresentation,
    isCorrect: boolean
): boolean => {
    return (
        !isQuestionRetriable(element, presentation) ||
        isQuestionRetryLimitReached(elementInteractions, element, presentation, elementInteractions.answered, isCorrect)
    );
};

const createFinalizedInteraction = (
    answers: IShape[],
    correctAnswers: number[],
    markedAreas: IShape[],
    elementInteractions: MarkupImageElementInteractions
): MarkupImageElementInteractions => {
    return {
        answered: true,
        answeredCorrectly: correctAnswers.length === markedAreas.length,
        correctAnswers,
        answers,
        retriedAnswers: elementInteractions.retriedAnswers || [],
    };
};

const createRetryInteraction = (
    answers: IShape[],
    correctAnswers: number[],
    markedAreas: IShape[],
    elementInteractions: MarkupImageElementInteractions
): MarkupImageElementInteractions => {
    return {
        answered: false,
        answeredCorrectly: correctAnswers.length === markedAreas.length,
        markedAnswers: [],
        correctAnswers,
        retriedAnswers: [...(elementInteractions.retriedAnswers || []), {attempt: {answers}}],
    };
};

const createElementInteraction = (
    answers: IShape[],
    correctAnswers: number[],
    element: IElementImageMarkup,
    elementInteractions: MarkupImageElementInteractions,
    presentation: IPresentation
): MarkupImageElementInteractions => {
    const {markedAreas} = element;
    const isCorrect = correctAnswers.length === markedAreas.length;

    if (shouldFinalizeAnswer(element, elementInteractions, presentation, isCorrect)) {
        return createFinalizedInteraction(answers, correctAnswers, markedAreas, elementInteractions);
    }
    return createRetryInteraction(answers, correctAnswers, markedAreas, elementInteractions);
};

const calculateNewPoints = (
    currentPoints: number,
    elementInteraction: MarkupImageElementInteractions,
    element: IElementImageMarkup,
    gameConfig: IGameConfig | null
): number => {
    const pointsResult = element.isDebatable ? 'debatable' : elementInteraction.answeredCorrectly;

    return getNewPoints(currentPoints, pointsResult, gameConfig || ({} as IGameConfig), element.gameConfig);
};

function useImageMarkupTry(): (element: IElementImageMarkup, elementInteractions: MarkupImageElementInteractions) => void {
    const dispatch = useDispatch();

    const {refsMap} = useImageMarkupContext();
    const saveRunfile = useSaveRunfile();

    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);
    const presentation = useSelector(loadedPresentationSelector);

    function handleTry(element: IElementImageMarkup, elementInteractions: MarkupImageElementInteractions): void {
        if (!presentation) {
            return;
        }

        if (elementInteractions.answered) {
            return;
        }

        const markupRefs = refsMap[element.id];
        if (!markupRefs) return;

        const userMarkedShapes = elementInteractions.markedAnswers || [];

        const {answers, correctAnswers} = validateUserShapes(userMarkedShapes, element, markupRefs);

        const elementInteraction = createElementInteraction(answers, correctAnswers, element, elementInteractions, presentation);

        const newPoints = calculateNewPoints(points || DEFAULT_POINTS, elementInteraction, element, gameConfig || null);

        saveRunfile(elementInteraction, element.id, element.slideId, newPoints);

        const handleElementsClickedActionCreator = (): AnyAction => handleElementsClickedAction([element.id], !!element.clearAfterClicked);

        dispatch(handleElementsClickedActionCreator());
    }

    return handleTry;
}

export default useImageMarkupTry;
